// Performance as per Dispatch — Feb 2026 (v2).
//
// Aggregate view mirroring the Cost Sheet Editor's Cost Break-up + Pie,
// weighted by ACTUAL dispatched + rejected quantity per part for the
// selected month/year. Rejection is added because those pieces were
// physically produced and consumed material + process capacity, so
// they belong in the actual manufacturing spend.

import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { ChartBar, Warning } from "@phosphor-icons/react";
import { MONTH_NAMES } from "../contexts/MonthContext";
import { API, useProcesses } from "../App";
import {
  aggregateCostBreakup,
  CostBreakupCard,
  ContextPill,
} from "./BudgetSchedule";

const rupee = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const num = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const PIE_COLORS = ["#1D2951", "#D4A843", "#0EA5E9", "#F97316", "#8B5CF6",
                    "#10B981", "#EF4444", "#EC4899", "#14B8A6", "#F59E0B",
                    "#6366F1", "#84CC16"];

// Same helper as BudgetSchedule uses — kept local because the file already
// imports the shared aggregate engine.
function useProcessLabels() {
  const { opNames } = useProcesses();
  return (key) => {
    const clean = (key || "").replace(/__\d+$/, "");
    if (opNames?.[clean]) return opNames[clean];
    return clean.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  };
}

export default function PerformanceDispatch() {
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);  // default = current
  const [sales, setSales] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [rejByPart, setRejByPart] = useState({});
  const [rejCost, setRejCost] = useState({ total_cost: 0, total_qty: 0, by_part: {}, by_process: {} });
  const [loading, setLoading] = useState(true);
  const processLabelFor = useProcessLabels();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Period-scoped sales register so dispatch_qty is the period allocation
      const params = month ? `?month=${month}&year=${year}` : "";
      const [sr, cs, rj, rc] = await Promise.all([
        axios.get(`${API}/sales-register${params}`,                 { skipPeriod: true }),
        axios.get(`${API}/cost-sheets?full=true`,                   { skipPeriod: true }),
        axios.get(`${API}/transactions/rejection-summary${params}`, { skipPeriod: true }),
        axios.get(`${API}/analytics/rejection-cost${params}`,       { skipPeriod: true }),
      ]);
      setSales(sr.data?.data || []);
      setSheets(cs.data || []);
      setRejByPart(rj.data?.by_part || {});
      setRejCost(rc.data || { total_cost: 0, total_qty: 0, by_part: {}, by_process: {} });
    } catch { toast.error("Failed to load Dispatch Performance"); }
    finally { setLoading(false); }
  }, [month, year]);
  useEffect(() => { load(); }, [load]);

  // Multiplier per part = DISPATCHED qty only. Rejected pieces are
  // accounted for separately via the real rejection-cost endpoint so
  // they don't inflate every process line above the reject stage.
  const qtyByPart = useMemo(() => {
    const m = new Map();
    sales.forEach(r => {
      const q = Number(r.dispatch_qty || 0);
      if (!q) return;
      const key = r.part_id || `no:${r.part_no}`;
      m.set(key, (m.get(key) || 0) + q);
    });
    return m;
  }, [sales]);

  // Context strip values
  const dispatchTotal  = useMemo(() =>
    sales.reduce((s, r) => s + Number(r.dispatch_qty || 0), 0),
  [sales]);
  const rejectionTotal = useMemo(() =>
    Object.values(rejByPart).reduce((s, v) => s + Number(v || 0), 0),
  [rejByPart]);

  const agg = useMemo(() => aggregateCostBreakup({
    costSheets: sheets, qtyByPart, processLabelFor,
  }), [sheets, qtyByPart, processLabelFor]);

  const pieData = useMemo(() => {
    return [
      { name: "Raw Material", value: agg.raw_material_cost, key: "raw_material" },
      ...agg.process_lines.map(p => ({ name: p.label, value: p.cost, key: p.key })),
    ].filter(d => d.value > 0);
  }, [agg]);

  const yearOpts = useMemo(() => {
    const s = new Set([new Date().getFullYear()]);
    // Include years derived from dispatch history dates by pulling from
    // delivery_date on the always-loaded register.
    sales.forEach(r => {
      const y = Number((r.delivery_date || "").slice(0, 4));
      if (y) s.add(y);
    });
    return Array.from(s).sort((a, b) => b - a);
  }, [sales]);

  const periodLabel = month
    ? `${MONTH_NAMES[month - 1]} ${year}`
    : `All months · ${year}`;

  return (
    <Layout title="Performance as per Dispatch">
      <div className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-[#1D2951]/5 text-[#1D2951] flex-shrink-0">
              <ChartBar size={22} weight="fill" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1D2951]" style={{ fontFamily: "Chivo" }}>
                Performance as per Dispatch
              </h1>
              <p className="text-xs md:text-sm text-[#64748B] mt-0.5">
                Actual cost break-up &amp; distribution from dispatched quantities, with real rejection loss (reject qty × cost-at-reject).
              </p>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#64748B] block mb-1">Month</label>
              <Select value={String(month)} onValueChange={v => setMonth(Number(v))}>
                <SelectTrigger className="h-9 w-32" data-testid="perf-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">All months</SelectItem>
                  {MONTH_NAMES.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#64748B] block mb-1">Year</label>
              <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                <SelectTrigger className="h-9 w-24" data-testid="perf-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {yearOpts.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Context strip */}
        <Card className="p-3 flex flex-wrap items-center gap-x-6 gap-y-2 border border-[#E2E8F0] text-xs">
          <ContextPill label="Period" value={periodLabel} />
          <ContextPill label="Dispatched Qty" value={num(dispatchTotal)} />
          <ContextPill label="Rejected Qty" value={num(rejectionTotal)} tone={rejectionTotal > 0 ? "#B91C1C" : "#1D2951"} />
          <ContextPill label="Rejection Cost" value={rupee(rejCost.total_cost)} tone={rejCost.total_cost > 0 ? "#B91C1C" : "#1D2951"} />
          <ContextPill label="Parts Covered" value={agg.parts_covered} />
        </Card>

        {agg.total_qty === 0 && rejCost.total_cost === 0 && !loading && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-[#FEF3C7] border border-[#F59E0B] text-xs text-[#78350F]"
            data-testid="perf-empty-warning">
            <Warning size={16} weight="fill" className="text-[#B45309]" />
            <span>No dispatches or rejections in this period — try a different month/year.</span>
          </div>
        )}

        {/* Break-up + Pie */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CostBreakupCard agg={agg} loading={loading}
            title="Cost Break-up (Actual)" testPrefix="pd"
            rejectionOverride={rejCost.total_cost} />
          <Card className="p-5 border border-[#E2E8F0]">
            <h3 className="text-sm font-bold text-[#1D2951] mb-3">Cost Distribution</h3>
            {pieData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm text-[#94A3B8]">
                {loading ? "Loading…" : "No cost data for the selected period."}
              </div>
            ) : (
              <div className="h-80" data-testid="pd-pie">
                <ResponsiveContainer>
                  <PieChart margin={{ top: 24, right: 24, bottom: 8, left: 24 }}>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      outerRadius={80} innerRadius={40} paddingAngle={2}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: "#94A3B8", strokeWidth: 1 }}>
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => rupee(v)} />
                    <Legend verticalAlign="bottom" height={36} iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}
